import _ from 'lodash';
import semver from 'semver';
import { nanoid } from 'nanoid';
import Convert from 'ansi-to-html';
import { CHANGES_STATE } from '../../actions/studio/fields/types';
import config from '../../../config';
import logger from '../logging/logger';
import type { RootDocument } from '../../types/Field';
import type { ObjectDefinition } from '../../types/Schema';
import {
    fieldPathToString,
    findCollectionInList,
    findModelInList,
    findObjectInList,
    getFieldFromFieldPath,
    getFieldModelFromFieldPath,
    getRootDocument
} from './utils-ts';
import { iterateRootField } from './iterate-fields';

export const getContainingLocale = (object: $TSFixMe, fieldPath: $TSFixMe) => {
    let locale;
    for (let i = fieldPath.length; i >= 2; i--) {
        if (!fieldPathPartIsKeyword(fieldPath.slice(0, i), fieldPath)) {
            const field = _.get(object, fieldPath.slice(1, i));
            locale = field?.locale;
            if (locale) {
                break;
            }
        }
    }
    return locale;
};

export const fieldPathPartIsKeyword = (fieldPath: $TSFixMe, fullFieldPath: $TSFixMe) => {
    const curPart = _.last(fieldPath);
    const nextPart = fullFieldPath && fullFieldPath[fieldPath.length];
    const isItemsKeyword = curPart === 'items' && (nextPart ? typeof nextPart === 'number' : true);
    return curPart === 'fields' || isItemsKeyword;
};

export const addFieldPathToFieldPaths = (fieldPath: $TSFixMe, fieldPaths: $TSFixMe) =>
    _.reduce(
        fieldPath,
        (res, cur, index) => {
            // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
            const curFieldPath = _.take(fieldPath, index + 1);
            if (fieldPathPartIsKeyword(curFieldPath, fieldPath)) {
                return res;
            }
            // @ts-expect-error TS(2345): Argument of type 'unknown[]' is not assignable to ... Remove this comment to see the full error message
            const curFieldPathStr = fieldPathToString(curFieldPath);
            if (!res.includes(curFieldPathStr)) {
                res.push(curFieldPathStr);
            }
            return res;
        },
        fieldPaths
    );

/**
 * Find field by fieldPath in array of { fieldPath, field } objects
 * @example
 * const fields = [
 *  { fieldPath: [0, 'fields', 'test'], field: { value: 'test' } },
 *  { fieldPath: [0, 'fields', 'slug'], field: { value: 'page' } },
 * ];
 * const fieldPathToFind = [0, 'fields', 'test'];
 * const { field, fieldPath } = findFieldByFieldPath(fields, fieldPathToFind) || {};
 */
export const findFieldByFieldPath = (arrFieldPaths = [], seekingFieldPath: $TSFixMe, findAll = false) => {
    const method = findAll ? 'filter' : 'find';
    return arrFieldPaths[method](({ fieldPath }) => _.isEqual(fieldPath, seekingFieldPath));
};

export const getFieldObject = (fieldData: $TSFixMe, fieldPath: $TSFixMe) => _.omit(resolveFieldData(fieldData, fieldPath), ['srcFieldNames']);

export const getSrcProjectId = (fields: $TSFixMe, fieldPath: $TSFixMe) => {
    const rootObject = getRootDocument(fields, fieldPath[0]);
    return _.get(rootObject, 'srcProjectId');
};

/**
 * Recursively resolve field data, such as srcObjectId, srcProjectId and fieldPath.
 *
 * The srcProjectId taken from the first parent that have it.
 * The srcObjectId taken from the first parent that have it, independently to srcProjectId.
 * The fieldPath is relative to the first parent that have srcObjectId, the list's 'items'
 * and object's 'fields' properties are filtered out.
 * The fieldDataPath is relative to the first parent that have srcObjectId, the list's 'items'
 * and object's 'fields' properties are reserved. The first item is srcObjectId.
 *
 * @example
 * fieldData = [{
 *   "type": "object",
 *   "srcModelName": "home",
 *   "srcType": "git",
 *   "srcObjectId": "id_of_home"
 *   "srcProjectId": "y",
 *   "fields": {
 *     "title": { "type": "string", "value": "Home" },
 *     "hero": {
 *       "type": "object",
 *       "srcModelName": "hero",
 *       "srcObjectId": "id_of_hero",
 *       "fields": {
 *         "title": { "type": "string", "value": "I am a Hero" }
 *       }
 *     },
 *     "tags": {
 *       "type": "list",
 *       "items": ['foo', 'bar']
 *     },
 *     "sections": {
 *       "type": "list",
 *       "items": [{
 *         "type": "object",
 *         "srcModelName": "features",
 *         "fields": {
 *           "title": { "type": "string", "value": "I am a Hero" }
 *         }
 *       }]
 *     }
 *   }
 * }]
 *
 * resolveFieldData(fieldData, [0, 'fields', 'hero']) => {
 *   fieldPath: [],
 *   srcType: "git",
 *   srcObjectId: "id_of_hero",
 *   srcProjectId: "y"
 * }
 *
 * resolveFieldData(fieldData, [0, 'fields', 'hero', 'fields', 'title']) => {
 *   fieldPath: ['title'],
 *   srcType: "git",
 *   srcObjectId: "id_of_hero",
 *   srcProjectId: "y"
 * }
 *
 * resolveFieldData(fieldData, [0, 'fields', 'tags', 'items', 1]) => {
 *   fieldPath: ['tags', 1],
 *   srcType: "git",
 *   srcObjectId: "id_of_home",
 *   srcProjectId: "y"
 * }
 *
 * resolveFieldData(fieldData, [0, 'fields', 'sections', 'items', 0, 'fields', 'title']) => {
 *   fieldPath: ['sections', 0, 'title'],
 *   srcType: "git",
 *   srcObjectId: "id_of_home",
 *   srcProjectId: "y"
 * }
 *
 * @param {Array} fieldData
 * @param {Array} fieldDataPath
 * @param {Boolean} [initialRun]
 */
export const resolveFieldData = (fieldData: $TSFixMe, fieldDataPath: $TSFixMe, initialRun = true) => {
    const field = getFieldFromFieldPath(fieldData, fieldDataPath);
    let fieldPath: $TSFixMe = [];
    let relativeFieldDataPath: $TSFixMe = [];
    let srcType = _.get(field, 'srcType');
    let srcObjectId = _.get(field, 'srcObjectId');
    let srcProjectId = _.get(field, 'srcProjectId');
    let srcEnvironment = _.get(field, 'srcEnvironment');
    const srcFieldNames = _.get(field, 'srcFieldNames');
    if (initialRun) {
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'undefined'.
        srcObjectId = null;
    }
    if (srcObjectId) {
        relativeFieldDataPath.push(srcObjectId);
    }
    if (!srcObjectId || !srcProjectId) {
        // always drop two last items
        // for objects: last item is field name, and one before last is 'fields'
        // => fieldData[<index>].fields.hero.fields.title
        // for lists of strings: last item is index, and one before last is 'items'
        // => fieldData[<index>].fields.sections.list[<index>] = [ strings ]
        // for lists of objects, field name => fields => list item index => list => etc.
        // => fieldData[<index>].fields.sections.list[<index>].fields.title
        const parentFieldKeyPath = _.dropRight(fieldDataPath, 2);
        const parentField = parentFieldKeyPath.length ? resolveFieldData(fieldData, parentFieldKeyPath, false) : {};
        if (!srcObjectId) {
            let fieldName = _.last(fieldDataPath);
            if (srcFieldNames) {
                // @ts-expect-error TS(2769): No overload matches this call.
                fieldName = _.get(srcFieldNames, fieldName, fieldName);
            }
            // @ts-expect-error TS(2339): Property 'srcObjectId' does not exist on type '{}'... Remove this comment to see the full error message
            srcObjectId = parentField.srcObjectId;
            // @ts-expect-error TS(2339): Property 'fieldPath' does not exist on type '{}'.
            fieldPath = _.concat(parentField.fieldPath, fieldName);
            // @ts-expect-error TS(2339): Property 'fieldDataPath' does not exist on type '{... Remove this comment to see the full error message
            relativeFieldDataPath = _.concat(parentField.fieldDataPath, _.takeRight(fieldDataPath, 2));
        }
        if (!srcProjectId) {
            // @ts-expect-error TS(2339): Property 'srcProjectId' does not exist on type '{}... Remove this comment to see the full error message
            srcProjectId = parentField.srcProjectId;
        }
        if (!srcEnvironment) {
            // @ts-expect-error TS(2339): Property 'srcEnvironment' does not exist on type '... Remove this comment to see the full error message
            srcEnvironment = parentField.srcEnvironment;
        }
        if (!srcType) {
            // @ts-expect-error TS(2339): Property 'srcType' does not exist on type '{}'.
            srcType = parentField.srcType;
        }
    }
    return {
        fieldPath,
        fieldDataPath: relativeFieldDataPath,
        srcType,
        srcObjectId,
        srcProjectId,
        srcEnvironment,
        srcFieldNames
    };
};

export const getElementMatchesFromFieldMap = (fieldPathStr: $TSFixMe, pathMap: $TSFixMe) => _.get(pathMap, fieldPathStr);

export const getModelsForFieldCreation = (fieldModel: $TSFixMe, { collections, schemaModels, srcType, srcProjectId, selectedLocale }: $TSFixMe) => {
    const isLinkingAllowed =
        (fieldModel.type === 'list' && (fieldModel.items.type === 'reference' || fieldModel.items.type === 'cross-reference')) ||
        fieldModel.type === 'reference' ||
        fieldModel.type === 'cross-reference';

    const models = getModelsForFieldModel({ fieldModel, modelLocation: { srcType, srcProjectId }, schemaModels }).filter((model: $TSFixMe) => {
        if (!model) {
            logger.warn('getModelsForFieldCreation: Corrupted model', { model });
            return null;
        }
        return model;
    });

    const modelsForCreate = models.filter((model: $TSFixMe) => {
        if (model.readOnly || model.hidden) {
            return false;
        }
        if (selectedLocale) {
            // check if object has localized fields - and we being on locale - filter it out
            const hasLocalizedField = model.fields?.some((field: $TSFixMe) => field.localized);
            if (hasLocalizedField) {
                return false;
            }
        }
        const collection = findCollectionInList(collections, model);
        // @ts-expect-error TS(2339): Property 'objects' does not exist on type 'ObjectS... Remove this comment to see the full error message
        if (model.singleInstance && collection?.objects.length > 0) {
            return false;
        }
        return true;
    });

    const linkableObjects = models.reduce((acc: $TSFixMe, cur: $TSFixMe) => {
        if (cur.hidden) {
            return acc;
        }
        // @ts-expect-error TS(2339): Property 'objects' does not exist on type 'ObjectS... Remove this comment to see the full error message
        const modelObjects = findCollectionInList(collections, cur)?.objects ?? [];
        const linkableObjectsForModel = _.map(modelObjects, (item) => ({ ...item, modelLabel: cur.label, modelName: cur.name }));
        acc.push(...linkableObjectsForModel);
        return acc;
    }, []);

    return { models, modelsForCreate, linkableObjects, isLinkingAllowed };
};

// @ts-expect-error TS(7023): 'getModelsForFieldModel' implicitly has return typ... Remove this comment to see the full error message
export const getModelsForFieldModel = ({
    fieldModel,
    modelLocation,
    schemaModels,
    parentFieldModel
}: {
    fieldModel: $TSFixMe;
    modelLocation: $TSFixMe;
    schemaModels: $TSFixMe;
    parentFieldModel?: $TSFixMe;
}) => {
    const { srcType, srcProjectId } = modelLocation;

    if (!fieldModel) {
        return [];
    }

    if (fieldModel.type === 'list') {
        if (_.isArray(fieldModel.items)) {
            // this case only handles when schema looks like list of models
            // fieldModel = {
            //     type: 'list',
            //     items: [{
            //         type: 'model',
            //         model: 'one'
            //     }, {
            //         type: 'model',
            //         model: 'two'
            //     }, {
            //         type: 'model',
            //         model: 'three'
            //     }]
            // }
            //
            // @todo make it works with more different formats, like
            // fieldModel = {
            //     type: 'list',
            //     items: [{
            //         type: 'reference',
            //         models: ['one', 'two']
            //     }, {
            //         type: 'model',
            //         model: 'three'
            //     }]
            // }
            return fieldModel.items.map(
                (
                    itemFieldModel: $TSFixMe // will always return array of one element
                ) => _.head(getModelsForFieldModel({ fieldModel: itemFieldModel, modelLocation, schemaModels, parentFieldModel: fieldModel }))
            );
        }
        return getModelsForFieldModel({ fieldModel: fieldModel.items, modelLocation, schemaModels, parentFieldModel: fieldModel });
    }

    let modelNames;
    let models;

    if (fieldModel.type === 'reference') {
        if (fieldModel.models.length) {
            modelNames = fieldModel.models;
        } else {
            // that means any model in current content source
            models = schemaModels.filter((model: $TSFixMe) => model.srcType === srcType && model.srcProjectId === srcProjectId);
        }
    } else if (fieldModel.type === 'cross-reference') {
        models = fieldModel.models.map((model: $TSFixMe) => findModelInList(schemaModels, { ...model, name: model.modelName })).filter(Boolean);
    } else if (fieldModel.type === 'models') {
        modelNames = fieldModel.models;
    } else if (fieldModel.type === 'model') {
        modelNames = fieldModel.model ? [fieldModel.model] : fieldModel.models || [];
    } else if (['object', 'page', 'data'].includes(fieldModel.type)) {
        models = [fieldModel];
        if (fieldModel.name && !fieldModel.fields) {
            modelNames = [fieldModel.name];
        } else if (!fieldModel.name) {
            models = [
                {
                    ...fieldModel,
                    name: parentFieldModel?.name ?? 'Object'
                }
            ];
        }
    } else {
        // children type is primitive
        models = [
            {
                type: fieldModel.type,
                options: fieldModel.options
            }
        ];
    }

    if (modelNames) {
        models = modelNames.map((name: $TSFixMe) => findModelInList(schemaModels, { name, srcProjectId, srcType })).filter(Boolean);
    }

    return models;
};

export const getNonEmptyObject = (list: $TSFixMe) => {
    if (!Array.isArray(list)) {
        return undefined;
    }
    for (let i = 0; i < list.length; i++) {
        const field = list[i];
        if (field.fields && Object.keys(field.fields).length > 0) {
            return field;
        }
    }
    return undefined;
};

export const hasParentField = (fields: $TSFixMe, fieldPath: $TSFixMe) => {
    do {
        if (fields.includes(fieldPath)) {
            return true;
        }
        fieldPath = fieldPath.split('.').slice(0, -1).join('.');
    } while (fieldPath);
    return false;
};

export const createDOMMap = (element: $TSFixMe) =>
    [...element.childNodes].map((node) => {
        const details = {
            content: node.childNodes && node.childNodes.length > 0 ? null : node.textContent,
            node
        };
        // @ts-expect-error TS(2339): Property 'children' does not exist on type '{ cont... Remove this comment to see the full error message
        details.children = createDOMMap(node);
        return details;
    });

export const getDOMElByElementPath = (elementXPath: $TSFixMe, document = window.document) => {
    if (document.evaluate) {
        let node;
        try {
            node = document.evaluate(elementXPath, document.documentElement, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
        } catch (err) {
            logger.warn('getDOMElByElementPath: Could not find node by xpath', { xpath: elementXPath });
        }
        if (node) {
            if (node instanceof Attr) {
                return { node: node.ownerElement, source: 'attribute', attribute: node };
            } else if (node.nodeType === Node.TEXT_NODE) {
                return { node: node.parentElement, source: 'text', text: node };
            }
            return { node, source: 'element' };
        }
        const parentNode = document.evaluate(
            elementXPath.split('/').slice(0, -1).join('/'),
            document.documentElement,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null
        ).singleNodeValue;
        if (parentNode) {
            return { node: parentNode, source: 'element' };
        }
    }

    return {};
};

export const getNodeXpath = (xpath: $TSFixMe) => {
    const parts = xpath.split('/');
    if (!parts[parts.length - 1]?.includes('@')) {
        return xpath;
    }
    return parts.slice(0, -1).join('/');
};

export const mobileWidth = 500;
export const isMobile = () => window.innerWidth <= mobileWidth;

export const request = (url: $TSFixMe, options = {}) => {
    const isFullUrl = url.indexOf('https://') === 0 || url.indexOf('http://') === 0;

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let requestBody;
        xhr.onload = () => (xhr.status >= 200 && xhr.status <= 204 ? resolve(xhr) : reject(xhr));
        xhr.onerror = reject;
        // @ts-expect-error TS(2339): Property 'includeCredentials' does not exist on ty... Remove this comment to see the full error message
        xhr.withCredentials = options.includeCredentials !== false;
        // @ts-expect-error TS(2339): Property 'method' does not exist on type '{}'.
        xhr.open(options.method || 'GET', isFullUrl ? url : config.api.base_url + url);
        // @ts-expect-error TS(2339): Property 'json' does not exist on type '{}'.
        if (options.json) {
            xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
            // @ts-expect-error TS(2339): Property 'json' does not exist on type '{}'.
            requestBody = JSON.stringify(options.json);
            // @ts-expect-error TS(2339): Property 'body' does not exist on type '{}'.
        } else if (options.body) {
            // @ts-expect-error TS(2339): Property 'body' does not exist on type '{}'.
            requestBody = options.body;
        }
        // @ts-expect-error TS(2339): Property 'headers' does not exist on type '{}'.
        if (options.headers) {
            // @ts-expect-error TS(2339): Property 'headers' does not exist on type '{}'.
            Object.keys(options.headers).forEach((header) => {
                // @ts-expect-error TS(2339): Property 'headers' does not exist on type '{}'.
                xhr.setRequestHeader(header, options.headers[header]);
            });
        }
        xhr.send(requestBody);
    });
};

export const getEditableObjects = (fieldData: RootDocument[]) => {
    const result: Array<ObjectDefinition & { srcEnvironment: string }> = [];

    iterateRootField(fieldData, (field, fieldPath, data) => {
        // nested object inside list don't have srcObjectId
        if (field.type === 'unresolved_reference' || field.type === 'cross-reference') {
            // @ts-expect-error TS(2322): Type 'ObjectDefinition | undefined' is not assigna... Remove this comment to see the full error message
            field = findObjectInList(fieldData, {
                srcObjectId: field.refId,
                // @ts-expect-error TS(2339): Property 'refSrcType' does not exist on type '({ t... Remove this comment to see the full error message
                srcType: field.refSrcType ?? data.srcType,
                // @ts-expect-error TS(2339): Property 'refProjectId' does not exist on type '({... Remove this comment to see the full error message
                srcProjectId: field.refProjectId ?? data.srcProjectId
            });
            if (!field) {
                // ignore unannotated references
                return true;
            }
        }

        // @ts-expect-error TS(2345): Argument of type 'Field | RootDocument' is not ass... Remove this comment to see the full error message
        if (findObjectInList(result, field)) {
            // stop cyclic references
            return true;
        }

        if (field.type === 'object') {
            const hasChildren = Object.values(field.fields ?? {}).length > 0;
            if (hasChildren) {
                // @ts-expect-error TS(2339): Property 'srcObjectId' does not exist on type 'Doc... Remove this comment to see the full error message
                if (field.srcObjectId) {
                    result.push({
                        // @ts-expect-error TS(2339): Property 'srcObjectId' does not exist on type 'Doc... Remove this comment to see the full error message
                        srcObjectId: field.srcObjectId,
                        // @ts-expect-error TS(2339): Property 'srcProjectId' does not exist on type 'Do... Remove this comment to see the full error message
                        srcProjectId: field.srcProjectId,
                        // @ts-expect-error TS(2339): Property 'srcEnvironment' does not exist on type '... Remove this comment to see the full error message
                        srcEnvironment: field.srcEnvironment,
                        // @ts-expect-error TS(2339): Property 'srcType' does not exist on type 'Documen... Remove this comment to see the full error message
                        srcType: field.srcType
                    });
                }
            }
        } else if (field.type === 'image' && field?.srcObjectId) {
            // @ts-expect-error TS(2339): Property 'srcProjectId' does not exist on type 'Im... Remove this comment to see the full error message
            const { srcObjectId, srcProjectId, srcType, srcEnvironment } = field;
            result.push({ srcObjectId, srcProjectId, srcEnvironment, srcType });
        }

        return undefined;
    });

    return result;
};

export const getParentNode = (el: $TSFixMe, selector: $TSFixMe, returnNodes: $TSFixMe) => {
    const nodes: $TSFixMe = [];
    const emptyReturn = returnNodes ? { node: null, nodes } : null;
    if (!el) {
        return emptyReturn;
    }
    if (el.nodeType === Node.TEXT_NODE) {
        el = el.parentElement; // eslint-disable-line
    }
    if (!el.matches) {
        return emptyReturn;
    }
    do {
        if (el.matches(selector)) {
            return returnNodes ? { node: el, nodes } : el;
        }
        nodes.push(el);
    } while ((el = el.parentElement)); // eslint-disable-line
    return returnNodes ? emptyReturn : null;
};

export const isEqualField = (field1: $TSFixMe, field2: $TSFixMe) =>
    field1?.srcObjectId && field2?.srcObjectId ? field1.srcObjectId === field2.srcObjectId : field1 === field2;

export const getScrollbarWidth = () => {
    const outer = document.createElement('div');
    outer.setAttribute('class', 'test');
    outer.style.visibility = 'hidden';
    outer.style.width = '100px';
    document.body.appendChild(outer);

    const widthNoScroll = outer.offsetWidth;
    outer.style.overflow = 'scroll';

    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);

    const widthWithScroll = inner.offsetWidth;
    // @ts-expect-error TS(2531): Object is possibly 'null'.
    outer.parentNode.removeChild(outer);

    return widthNoScroll - widthWithScroll;
};

let currentFocus: $TSFixMe;
const focusPollers: $TSFixMe = [];

const startPollingFocus = () => {
    if (typeof currentFocus === 'undefined') {
        currentFocus = document.hasFocus();
    }
    if (document.hasFocus() !== currentFocus) {
        currentFocus = document.hasFocus();
        // @ts-expect-error TS(7006): Parameter 'fn' implicitly has an 'any' type.
        focusPollers.forEach((fn) => fn(currentFocus));
    }
    if (focusPollers.length) {
        setTimeout(startPollingFocus, 250);
    }
};

export const pollDocumentFocus = (cb: $TSFixMe) => {
    if (focusPollers.indexOf(cb) === -1) {
        focusPollers.push(cb);
        startPollingFocus();
    }
};

export const stopPollingDocumentFocus = (cb: $TSFixMe) => {
    const index = focusPollers.indexOf(cb);
    if (index >= 0) {
        focusPollers.splice(index, 1);
    }
};

export const isMobileOrTablet = () => {
    let check = false;
    (function (a) {
        if (
            // eslint-disable-next-line max-len
            /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
                a
            ) ||
            // eslint-disable-next-line max-len
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
                a.substr(0, 4)
            )
        )
            check = true;
        // @ts-expect-error TS(2339): Property 'opera' does not exist on type 'Window & ... Remove this comment to see the full error message
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check || navigator.maxTouchPoints > 0;
};

export const forEachPromise = (handlers: $TSFixMe, arg: $TSFixMe) =>
    handlers.reduce(
        (promise: $TSFixMe, nextHandler: $TSFixMe) =>
            promise.then((result: $TSFixMe) => {
                if (typeof result === 'boolean' && !result) {
                    return promise;
                }
                return nextHandler(arg);
            }),
        Promise.resolve()
    );

export const isShallowEqual = (v: $TSFixMe, o: $TSFixMe) => {
    let key;

    for (key in v) {
        if (!(key in o) || v[key] !== o[key]) {
            return false;
        }
    }

    for (key in o) {
        if (!(key in v) || v[key] !== o[key]) {
            return false;
        }
    }

    return true;
};

// Ref: https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
export const isWindowVisible = () => {
    if (typeof document === 'undefined') {
        return false;
    }

    if (typeof document.hidden !== 'undefined') {
        return !document.hidden;
    }

    // @ts-expect-error TS(2551): Property 'msHidden' does not exist on type 'Docume... Remove this comment to see the full error message
    if (typeof document.msHidden !== 'undefined') {
        // @ts-expect-error TS(2551): Property 'msHidden' does not exist on type 'Docume... Remove this comment to see the full error message
        return !document.msHidden;
    }

    // @ts-expect-error TS(2339): Property 'webkitHidden' does not exist on type 'Do... Remove this comment to see the full error message
    if (typeof document.webkitHidden !== 'undefined') {
        // @ts-expect-error TS(2339): Property 'webkitHidden' does not exist on type 'Do... Remove this comment to see the full error message
        return document.webkitHidden;
    }

    return true;
};

/**
 * Deeply freezes the provided object
 * @param {Object} object
 * @returns {Object}
 */
export const deepFreeze = (object: $TSFixMe) => {
    if (!object || typeof object !== 'object') {
        return object;
    }
    const propNames = Object.getOwnPropertyNames(object);

    for (const name of propNames) {
        const value = object[name];
        if (value && typeof value === 'object') {
            deepFreeze(value);
        }
    }

    return Object.freeze(object);
};

export const HUMAN_FRIENDLY_SOURCE_TYPE_NAMES = {
    contentful: 'Contentful',
    forestry: 'Forestry',
    sanity: 'Sanity',
    git: 'Git',
    netlifycms: 'NetlifyCMS',
    datocms: 'DatoCMS'
};

const colorConverter = new Convert({
    colors: {
        1: '#E4786D',
        2: '#0E0',
        3: '#F5BF7B',
        4: '#D7BEF7',
        5: '#BC6796',
        6: '#60BEC4',
        7: '#EEE',
        8: '#999'
    }
});
export const getHtmlFromAnsiString = (string: $TSFixMe) => {
    // ESC[1G not supported
    // eslint-disable-next-line no-control-regex
    const cleanString = string.replace(/\u001b\[1G/g, '');
    return colorConverter.toHtml(cleanString);
};

export const performClickOnRef = (ref: $TSFixMe, e: $TSFixMe) => {
    if (e.target !== ref.current) {
        _.get(ref, 'current.click', () => {}).apply(ref.current);
    }
};

export const copyStyles = (sourceDoc: $TSFixMe, targetDoc: $TSFixMe) => {
    Array.from(sourceDoc.styleSheets).forEach((styleSheet) => {
        let cssRules;
        try {
            // @ts-expect-error TS(2571): Object is of type 'unknown'.
            cssRules = styleSheet.cssRules;
            if (cssRules) {
                // true for inline styles
                const newStyleEl = targetDoc.createElement('style');

                Array.from(cssRules).forEach((cssRule) => {
                    // @ts-expect-error TS(2571): Object is of type 'unknown'.
                    newStyleEl.appendChild(targetDoc.createTextNode(cssRule.cssText));
                });

                targetDoc.head.appendChild(newStyleEl);
            }
        } catch (e) {} // eslint-disable-line no-empty
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        if (!cssRules && styleSheet.href) {
            // true for stylesheets loaded from a URL
            const newLinkEl = targetDoc.createElement('link');

            newLinkEl.rel = 'stylesheet';
            // @ts-expect-error TS(2571): Object is of type 'unknown'.
            newLinkEl.href = styleSheet.href;
            targetDoc.head.appendChild(newLinkEl);
        }
    });
};

export const applyChanges = (field: $TSFixMe, changes: $TSFixMe, { fields, schemaModels }: $TSFixMe) => {
    const { order, remove, value, fieldDataPath } = changes;
    const fieldPath = fieldDataPath.slice(1);
    if (typeof value !== 'undefined') {
        const curField = _.get(field, fieldPath);
        if (curField.type === 'list') {
            const fieldModel = getFieldModelFromFieldPath({
                fields,
                fieldPath: fieldDataPath,
                schemaModels,
                srcType: field.srcType,
                srcProjectId: field.srcProjectId
            });
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            if (fieldModel.items?.type === 'enum') {
                curField.items = value.map((curValue: $TSFixMe) => ({
                    type: 'enum',
                    value: curValue,
                    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                    options: fieldModel.items.options
                }));
            }
        } else {
            _.set(curField, 'value', value);
        }
    } else if (order) {
        const arrKeyPath = [...fieldPath, 'items'];
        const arr = _.get(field, arrKeyPath);
        const entryArr = arr.slice();
        const newEntryArr = order.map((newIndex: $TSFixMe) => entryArr[newIndex]);
        _.set(field, arrKeyPath, newEntryArr);
        _.set(field, [...fieldPath, 'changeApplyId'], nanoid());
    } else if (remove) {
        const { index } = remove;
        if (index) {
            const arrKeyPath = [...fieldPath, 'items'];
            const arr = _.get(field, arrKeyPath);
            arr.splice(index, 1);
            _.set(field, arrKeyPath, arr);
        }
    }
};

/**
 * Check if some field is updating and return it
 * @param {Array<String|number>|null} fieldPath Element to find. Pass null to find all/any
 * @param {Array<Object>} changes changedFields
 * @param {Object} [options=]
 * @param {Boolean} [returnAll=false] return first change only or all
 * @param {Boolean} [trackOnlyApi=false] return changes only which are in progress to the api
 * @returns {Object|Array<Object>}
 */
export const findUpdatingByFieldPath = (fieldPath: $TSFixMe, changes: $TSFixMe, { returnAll, trackOnlyApi }: $TSFixMe = {}) => {
    const statuses = [CHANGES_STATE.REQUEST_SENT];
    if (!trackOnlyApi) {
        // @ts-expect-error TS(2345): Argument of type '"REQUEST_SUCCESS"' is not assign... Remove this comment to see the full error message
        statuses.push(CHANGES_STATE.REQUEST_SUCCESS);
    }
    return changes[returnAll ? 'filter' : 'find'](
        (change: $TSFixMe) => statuses.includes(change.state) && (fieldPath ? _.isEqual(change.fieldPath, fieldPath) : true)
    );
};

/**
 * Check if some field is changed
 * @param {Array<String|number>|null} fieldPath Element to find. Pass null to find all/any
 * @param {Array<Object>} changes changedFields
 * @returns {Object|Array<Object>}
 */
export const findChangingByFieldPath = (fieldPath: $TSFixMe, changes: $TSFixMe) =>
    changes.find(
        (change: $TSFixMe) =>
            [CHANGES_STATE.REQUEST_SENT, CHANGES_STATE.REQUEST_SUCCESS, CHANGES_STATE.CHANGED].includes(change.state) &&
            (fieldPath ? _.isEqual(change.fieldPath, fieldPath) : true)
    );

export const getDomainField = (fields: $TSFixMe) => {
    // hardcoded domain field
    const configField = _.find(fields, { srcModelName: 'config' });
    let domainField = configField?.fields?.params?.fields?.domain;
    let domainFieldPath = configField ? [configField.srcObjectId, 'fields', 'params', 'fields', 'domain'] : null;
    if (configField && !domainField) {
        domainField = configField?.fields?.domain;
        domainFieldPath = [configField.srcObjectId, 'fields', 'domain'];
    }

    return { domainField, domainFieldPath };
};

export function countElementMatches(fieldTree: $TSFixMe, pathMap: $TSFixMe) {
    let fieldCount = 0;
    iterateRootField(fieldTree, () => {
        fieldCount++;
    });
    return { elementMatches: Object.keys(pathMap).length, fieldCount };
}

export const isSupportContentEditable = (fieldType: $TSFixMe) => ['string', 'slug', 'url', 'text', 'number'].includes(fieldType);

export function isAnnotationBased(stackbitVersion: $TSFixMe) {
    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    return stackbitVersion && semver.satisfies(semver.valid(semver.coerce(stackbitVersion)), '>=0.4.0');
}
